import { create } from "zustand";
import { useEffect } from "react";

export enum UserState {
    PROFILE_STATUS_UNKNOWN,
    PROFILE_STATUS_UNAUTHENTICATED,
    PROFILE_STATUS_NOT_ONBOARDED,
    PROFILE_STATUS_ACTIVATED,
    PROFILE_LOGIN_CANCELLED,
    PROFILE_ONBOARDING_CANCELLED,
    PROFILE_REACTIVATION_CANCELLED,
    PROFILE_STATUS_DEACTIVATED,
    PROFILE_STATUS_SUSPENDED,
}

export enum UserLoginActionState {
    CREATE_POST = "ck_create_post",
    CREATE_COMMENT = "ck_create_comment",
    CREATE_REACTION = "ck_create_reaction",
    REPORT_CONTENT = "ck_report",
    NOTIFICATION_PAGE_TEASER = "ck_notifications",
}

interface UserSlice {
    detailedUserState: UserState;
    isInActivationProcess: () => boolean;
    isLoggedIn: () => boolean;
    setUserAuthContextState: (value: UserLoginActionState) => void;
    setUserState: (value: UserState) => void;
    userState: UserState;
    login: (state: UserState, uuid?: string) => void;
    unauthenticate: () => void;
    userAuthContextState?: UserLoginActionState;
    userUuid?: string;
}

export const useUserStore = create<UserSlice>()((set, get) => ({
    userState: UserState.PROFILE_STATUS_UNKNOWN,
    detailedUserState: UserState.PROFILE_STATUS_UNKNOWN,
    userUuid: undefined,
    userAuthContextState: undefined,
    setUserState: (newValue: UserState) => {
        set(() => ({ detailedUserState: newValue }));
        let value = newValue;
        if (
            newValue === UserState.PROFILE_LOGIN_CANCELLED ||
            newValue === UserState.PROFILE_ONBOARDING_CANCELLED ||
            newValue === UserState.PROFILE_REACTIVATION_CANCELLED
        ) {
            value = UserState.PROFILE_STATUS_UNAUTHENTICATED;
        }
        set(() => ({ userState: value }));
    },
    setUserAuthContextState: (newValue: UserLoginActionState) =>
        set(() => ({ userAuthContextState: newValue })),
    isLoggedIn: () => checkIsLoggedIn(get().userState),
    isInActivationProcess: () =>
        get().userState >= UserState.PROFILE_STATUS_UNAUTHENTICATED &&
        get().userState !== UserState.PROFILE_STATUS_ACTIVATED &&
        get().userState < UserState.PROFILE_STATUS_SUSPENDED,
    login: (state, uuid) =>
        set(() => ({
            userUuid: uuid,
            userState: state,
        })),
    unauthenticate: () =>
        set(() => ({
            userUuid: undefined,
            userState: UserState.PROFILE_STATUS_UNAUTHENTICATED,
        })),
}));

export function checkIsLoggedIn(userState: UserState) {
    return (
        userState > UserState.PROFILE_STATUS_UNAUTHENTICATED &&
        userState <= UserState.PROFILE_STATUS_ACTIVATED
    );
}

export const useInterruptableAction = (
    check: any,
    completeAction: () => void,
    cancelAction?: () => void,
) => {
    const { detailedUserState: userState } = useUserStore();

    useEffect(() => {
        if (userState === UserState.PROFILE_STATUS_ACTIVATED) {
            check && completeAction();
        } else if (
            [
                UserState.PROFILE_LOGIN_CANCELLED,
                UserState.PROFILE_ONBOARDING_CANCELLED,
                UserState.PROFILE_REACTIVATION_CANCELLED,
                UserState.PROFILE_STATUS_SUSPENDED,
            ].indexOf(userState) !== -1
        ) {
            if (check) {
                cancelAction?.();
            }
        }
    }, [userState]);
};
